import React from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import { motion } from 'framer-motion';
import './MobileTechnologies.css';

const technologies = {
  ios: [
    { name: 'Swift', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg' },
    { name: 'SwiftUI', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg' },
    // { name: 'Objective-C', icon: '/assets/tech-logos/objective-c.png' },
    { name: 'Xcode', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/xcode/xcode-original.svg' },
    { name: 'CocoaPods', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg' },
    // { name: 'TestFlight', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg' }
  ],
  android: [
    { name: 'Kotlin', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg' },
    { name: 'Java', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
    { name: 'Android Studio', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg' },
    // { name: 'Jetpack', icon: '/assets/tech-logos/jetpack.png' },
    // { name: 'Gradle', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gradle/gradle-plain.svg' },
    { name: 'Material Design', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg' }
  ],
  crossPlatform: [
    { name: 'React Native', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
    { name: 'Flutter', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
    // { name: 'Ionic', icon: '/assets/tech-logos/ionic.png' },
    // { name: 'Xamarin', icon: '/assets/tech-logos/xamarin.png' },
    { name: 'TypeScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
    { name: 'Expo', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' }
  ],
  backend: [
    // { name: 'Firebase', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' },
    // { name: 'AWS Mobile', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg' },
    { name: 'MongoDB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
    { name: 'Node.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
    { name: 'GraphQL', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
    { name: 'REST APIs', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' }
  ]
};

const categories = [
  { id: 'ios', title: 'iOS Development', subtitle: 'Native iOS app development tools and frameworks' },
  { id: 'android', title: 'Android Development', subtitle: 'Native Android development technologies' },
  { id: 'crossPlatform', title: 'Cross-Platform', subtitle: 'Build once, deploy everywhere solutions' },
  { id: 'backend', title: 'Backend & APIs', subtitle: 'Mobile backend infrastructure and services' }
];

const MobileTechnologies = () => {
  return (
    <section className="tech-stack">
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="tech-header"
        >
          <Typography variant="h2" className="section-title" gutterBottom>
            Our Mobile Tech Stack
          </Typography>
          <Typography variant="subtitle1" className="ma-ts-section-subtitle" gutterBottom>
            Cutting-edge technologies for modern mobile applications
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {/* iOS and Android sections */}
          {categories.slice(0, 2).map((category, categoryIndex) => (
            <Grid item xs={12} key={category.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
              >
                <Box className="category-container">
                  <Typography variant="h5" className="ts-category-title">
                    {category.title}
                  </Typography>
                  <Typography variant="subtitle2" className="category-subtitle">
                    {category.subtitle}
                  </Typography>
                  <Grid container spacing={2} className="tech-grid">
                    {technologies[category.id].map((tech, techIndex) => (
                      <Grid item xs={6} sm={4} md={2} key={tech.name}>
                        <motion.div
                          className="tech-item"
                          whileHover={{ y: -5 }}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                        >
                          <img
                            src={tech.icon}
                            alt={tech.name}
                            className="tech-icon"
                          />
                          <Typography variant="body2" className="tech-name">
                            {tech.name}
                          </Typography>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </motion.div>
            </Grid>
          ))}

          {/* Cross-Platform and Backend sections side by side */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {categories.slice(2).map((category, categoryIndex) => (
                <Grid item xs={12} md={6} key={category.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (categoryIndex + 2) * 0.1 }}
                  >
                    <Box className="category-container" style={{ height: '100%' }}>
                      <Typography variant="h5" className="ts-category-title">
                        {category.title}
                      </Typography>
                      <Typography variant="subtitle2" className="category-subtitle">
                        {category.subtitle}
                      </Typography>
                      <Grid container spacing={2} className="tech-grid">
                        {technologies[category.id].map((tech, techIndex) => (
                          <Grid item xs={6} sm={4} key={tech.name}>
                            <motion.div
                              className="tech-item"
                              whileHover={{ y: -5 }}
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                            >
                              <img
                                src={tech.icon}
                                alt={tech.name}
                                className="tech-icon"
                              />
                              <Typography variant="body2" className="tech-name">
                                {tech.name}
                              </Typography>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </section>
  );
};

export default MobileTechnologies; 